/***
 * Created on 2023/8/8
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {formatDate, formatTime} from "../../../service/datetime";
import PasswordInput from "../../common/form/password_input";
import {useMutation} from "@tanstack/react-query";
import {apiPut} from "../../../service/api";
import md5 from "blueimp-md5";
import ChangeSuccessDialog from "./change_success_dialog";
import FailOverLimitDialog from "./fail_over_limit_dialog";
import {getErrorMessage} from 'common/ServiceCommon';
import {useBoundedStore} from 'common/Store';
import {getCookie} from "../../../service/common";


function ChangePassword(props) {
    const { t } = useTranslation();
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    const [oldPasswordStatus, setOldPasswordStatus] = useState(false);
    const [newPasswordStatus, setNewPasswordStatus] = useState(false);
    const [confirmPasswordStatus, setConfirmPasswordStatus] = useState(false);
    const [isAllPopulated, setIsAllPopulated] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showFailDialog, setShowFailDialog] = useState(false);
    const [clientId, setClientId] = useState(null);
    const [app, setApp] = useState(null);
    const store = useBoundedStore();

    const [inputValues, setInputValues] = useState({
        old_password: '',
        new_password: '',
        confirm_password: ''
    });

    const updatePasswordMutation = useMutation({
        mutationFn: async (data) => await apiPut(`/ajax/password`, data),
        onSuccess: data => {
            console.log('update success needs to show login dialog');
            setShowSuccessDialog(true);
        },
        onError: (error, variables, context) => {
            store.globalAlert.setAlert(getErrorMessage(t, error.cause?.code), 'error', true);
        }
    });

    const onChange = (e, callback, name=null, value=null) => {
        if (callback && typeof callback === 'function') {
            callback(e);
        }
        const _name = name ? name : e.target.name;
        const _value = value ? value: e.target.value;
        setInputValues(prev => {
            return {
                ...prev,
                [_name]: _value
            }
        })
    }

    const onBackToApp = () => {
        const url = new URLSearchParams(location.search);
        if (url.has('state')) {
            const state_url = new URLSearchParams(url.get('state'));
            return location.pathname = '/signin'
        }
        location.href = '/signin';
    }

    const onSubmit = () => {
        const data = {
            old_password: md5(inputValues.old_password),
            new_password: inputValues.new_password
        }
        updatePasswordMutation.mutate(data);
    }

     const getClientId = (url) => {
        const parsed_url = new URL(url);
        const state = parsed_url.searchParams.get('state');
        if (state) {
            const sub_params = state.split('?')?.[1];
            if (sub_params) {
                const parsed_sub_params = new URLSearchParams(sub_params);
                setClientId(parsed_sub_params.get('client_id'));
            }
        }
        return null;
    }

    const getApp = async () => {
        let session_apps = sessionStorage.getItem('apps');
        let app;
        if (session_apps) {
            session_apps = JSON.parse(session_apps);
            app = session_apps?.[clientId];
        }
        if (!app) {
            const resp = await fetch(`/ajax/app/${clientId}`);
            const res = await resp.json();
            setApp(res);
            if (!session_apps) {
                session_apps = {}
            }
            session_apps[clientId] = res;
            sessionStorage.setItem('apps', JSON.stringify(session_apps));
        }
        else {
            setApp(app);
        }
    }

    const goToLoginDevicePage = (e) => {
        e.preventDefault();
        location.hash = 'login_device';
        location.reload();
    }

    useEffect(() => {
        setIsAllPopulated(Object.values(inputValues).every(v => v !== ""));
    }, [Object.keys(inputValues)]);

    useEffect(() => {
        getClientId(window.location.href);
        if (clientId) {
            getApp();
        }
    }, [clientId]);

    return (
        <section className="page-body">
            <div className="page-content page-content--security__change-pwd">
                <section className="change-pwd--prompt">
                    {/*<header>{t("@Note@")}</header>*/}
                    <p className="text-14">
                        {t('@Note: Once you change your password, you will need to sign in you QNAP ID on all of you devices such as browsers, mobile devices and KoiBot, including when you sign in with Facebook or Google except QNAP devices that you have given account access.@')}&nbsp;
                        <a href="" onClick={goToLoginDevicePage}>{t('@View my logged in devices@')}</a>
                    </p>
                </section>
                <div className="change-pwd--form">
                    <div className="comp-content-wrapper">
                        <div className="form-group">
                            <label>{t('@Last Changed@')}</label>
                            <div className="val-text">{formatDate(t, currentUser.password_updated_at, true)} {formatTime(currentUser.password_updated_at)}</div>
                        </div>

                        <PasswordInput
                            label={t('@Old Password@')}
                            placeholder={t('@Old Password@')}
                            value={inputValues.old_password}
                            name="old_password"
                            onChange={onChange}
                            status={setOldPasswordStatus}
                            required={true}/>

                        <PasswordInput
                            label={t('@New Password@')}
                            placeholder={t('@New Password@')}
                            value={inputValues.new_password}
                            metadata={currentUser}
                            name="new_password"
                            onChange={onChange}
                            status={setNewPasswordStatus}
                            showStrength={true}
                            showTip={true}
                            ignoreRules={false}
                            required={true}/>

                        <PasswordInput
                            label={t('@Confirm New Password@')}
                            placeholder={t('@Confirm New Password@')}
                            value={inputValues.confirm_password}
                            matchInput={inputValues.new_password}
                            name="confirm_password"
                            onChange={onChange}
                            status={setConfirmPasswordStatus}
                            required={true} />

                        <div className="form-group--action-row">

                            {
                                getCookie('mobile_app') !== 'true' &&
                                <button type="button" className="q-btn q-btn--primary"
                                    disabled={!oldPasswordStatus || !newPasswordStatus || !confirmPasswordStatus || !isAllPopulated}
                                    onClick={onSubmit}>{t('@Submit@')}</button>
                            }

                            {
                                getCookie('mobile_app') === 'true' &&
                                <div className="q-btn-group">
                                    <button type="button" className="q-btn q-btn--primary"
                                            disabled={!oldPasswordStatus || !newPasswordStatus || !confirmPasswordStatus || !isAllPopulated}
                                            onClick={onSubmit}>{t('@Submit@')}</button>

                                    <button type="button" className="q-btn q-btn--primary" title={app?.display_name}
                                            onClick={onBackToApp}>{t('@Back to@')} {app?.display_name}</button>

                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>

            {showSuccessDialog &&
                <ChangeSuccessDialog showModal={showSuccessDialog} closeModal={() => setShowSuccessDialog(false)}/>}
            { showFailDialog && <FailOverLimitDialog showModal={showFailDialog} closeModal={() => setShowFailDialog(false)} />}
        </section>
    );
}

export default ChangePassword;